/**
 * Assets Tab - Upload and manage skin assets
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Upload,
  Image,
  Box,
  Trash2,
  Loader2,
} from 'lucide-react';
import type { SlotMachineSkinAsset, SkinAssetType } from '../../../public/deals/components/slot-machine/themes/skin-types';

interface AssetsTabProps {
  skinId: string;
  assets: SlotMachineSkinAsset[];
  isLoading: boolean;
}

const ASSET_CATEGORIES = [
  {
    title: 'Visual Assets',
    types: [
      { type: 'background' as const, label: 'Background', icon: Image, accept: 'image/*,video/*' },
      { type: 'cabinet_model' as const, label: 'Cabinet 3D Model', icon: Box, accept: '.glb,.gltf' },
      { type: 'cabinet_image' as const, label: 'Cabinet Image', icon: Image, accept: 'image/*' },
      { type: 'logo' as const, label: 'Logo', icon: Image, accept: 'image/*' },
      { type: 'loading_screen' as const, label: 'Loading Screen', icon: Image, accept: 'image/*' },
    ],
  },
  {
    title: 'Reel Assets',
    types: [
      { type: 'reel_divider' as const, label: 'Reel Dividers', icon: Image, accept: 'image/*,.glb,.gltf' },
      { type: 'reelspot_background' as const, label: 'Reelspot Background', icon: Image, accept: 'image/*' },
    ],
  },
  {
    title: 'Celebration Overlays',
    types: [
      { type: 'big_win_overlay' as const, label: 'Big Win Overlay', icon: Image, accept: 'image/*' },
      { type: 'mega_win_overlay' as const, label: 'Mega Win Overlay', icon: Image, accept: 'image/*' },
      { type: 'jackpot_overlay' as const, label: 'Jackpot Overlay', icon: Image, accept: 'image/*' },
    ],
  },
];

export default function AssetsTab({ skinId, assets, isLoading }: AssetsTabProps) {
  const queryClient = useQueryClient();
  const [uploadingType, setUploadingType] = useState<string | null>(null);

  const uploadMutation = useMutation({
    mutationFn: async ({ type, file }: { type: SkinAssetType; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('asset_type', type);

      const response = await fetch(`/api/slot-skins/${skinId}/assets`, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) throw new Error('Upload failed');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-slot-skin-assets', skinId] });
      setUploadingType(null);
    },
    onError: () => {
      setUploadingType(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (assetId: string) => {
      const response = await fetch(`/api/slot-skins/${skinId}/assets/${assetId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Delete failed');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-slot-skin-assets', skinId] });
    },
  });

  const getAssetForType = (type: SkinAssetType) => {
    return assets.find((a) => a.assetType === type);
  };

  const handleFileSelect = (type: SkinAssetType, file: File) => {
    setUploadingType(type);
    uploadMutation.mutate({ type, file });
  };

  if (!skinId) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>Save the skin first to upload assets.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold mb-4">Asset Management</h2>
        <p className="text-sm text-gray-500 mb-6">
          Upload images, videos, 3D models, and audio files for your slot machine.
        </p>
      </div>

      {ASSET_CATEGORIES.map((category) => (
        <div key={category.title}>
          <h3 className="text-md font-medium mb-4">{category.title}</h3>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {category.types.map(({ type, label, icon: Icon, accept }) => {
              const asset = getAssetForType(type);
              const isUploading = uploadingType === type;

              return (
                <div
                  key={type}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Icon size={16} className="text-gray-400" />
                      <span className="text-sm font-medium">{label}</span>
                    </div>
                    {asset && (
                      <button
                        onClick={() => deleteMutation.mutate(asset.id)}
                        disabled={deleteMutation.isPending}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>

                  {asset ? (
                    <div className="relative aspect-video bg-gray-100 rounded overflow-hidden">
                      {asset.mimeType?.startsWith('image/') ? (
                        <img
                          src={asset.fileUrl}
                          alt={label}
                          className="w-full h-full object-cover"
                        />
                      ) : asset.mimeType?.startsWith('video/') ? (
                        <video
                          src={asset.fileUrl}
                          className="w-full h-full object-cover"
                          muted
                          loop
                          autoPlay
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-400">
                          <Box size={32} />
                        </div>
                      )}
                      <span className="absolute bottom-1 right-1 bg-black/50 text-white text-xs px-2 py-0.5 rounded">
                        {asset.originalFilename?.split('.').pop()?.toUpperCase()}
                      </span>
                    </div>
                  ) : (
                    <label className="block">
                      <input
                        type="file"
                        accept={accept}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileSelect(type, file);
                        }}
                        className="hidden"
                        disabled={isUploading}
                      />
                      <div className="aspect-video bg-gray-50 border-2 border-dashed border-gray-300 rounded flex flex-col items-center justify-center cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-colors">
                        {isUploading ? (
                          <Loader2 className="animate-spin text-primary-500" size={24} />
                        ) : (
                          <>
                            <Upload size={24} className="text-gray-400 mb-2" />
                            <span className="text-xs text-gray-500">Click to upload</span>
                          </>
                        )}
                      </div>
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
